import React, { useCallback, memo, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plus, X, Camera } from 'lucide-react';
import Button from '../shared/Button';
import Input from '../shared/Input';
import SearchSelect from '../shared/SearchSelect';
import ImageBrowser from '../shared/ImageBrowser';
import PaymentScheduleRulesEditor from './PaymentScheduleRulesEditor';

// Individual array item component to prevent re-renders
const ArrayItem = memo(({ item, index, field, placeholder, onChange, onRemove }) => (
  <div className="flex items-center space-x-2">
    <Input
      value={item}
      onChange={(e) => onChange(field, index, e.target.value)}
      placeholder={placeholder}
      className="flex-1"
    />
    <button
      type="button"
      onClick={() => onRemove(field, index)}
      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
      title="Remove item"
    >
      <X className="w-4 h-4" />
    </button>
  </div>
));

// Dynamic array field component - moved outside to prevent recreation
const DynamicArrayField = ({ 
  label, 
  field, 
  placeholder, 
  description, 
  items = [], 
  onAdd, 
  onRemove, 
  onChange 
}) => {
  // Ensure items is always an array
  const safeItems = Array.isArray(items) ? items : [];
  
  return (
    <div className="sm:col-span-2">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <div className="space-y-2">
        {safeItems.map((item, index) => (
          <ArrayItem
            key={`${field}-${index}`}
            item={item}
            index={index}
            field={field}
            placeholder={placeholder}
            onChange={onChange}
            onRemove={onRemove}
          />
        ))}
        <button
          type="button"
          onClick={() => onAdd(field)}
          className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 text-sm font-medium py-2 px-3 border border-primary-300 rounded-md hover:bg-primary-50 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add {label.toLowerCase().split(' ')[0]}</span>
        </button>
      </div>
      <p className="text-xs text-gray-500 mt-1">
        {description}
      </p>
    </div>
  );
};

// Memoize the DynamicArrayField to prevent unnecessary re-renders
const MemoizedDynamicArrayField = memo(DynamicArrayField);

const ServicePackageModal = ({
  isOpen,
  isEdit,
  formData,
  services,
  selectedService,
  onClose,
  onSubmit,
  onInputChange,
  onArrayFieldChange,
  onAddArrayItem,
  onRemoveArrayItem,
  onImageSelect,
  onPaymentScheduleRulesChange,
  isLoading,
  maxPackagesPerService = null,
  existingPackages = [],
}) => {
  const [showImageBrowser, setShowImageBrowser] = useState(false);

  const packageCountForSelectedService = useMemo(() => {
    if (!formData.service_id || !Array.isArray(existingPackages)) return 0;
    return existingPackages.filter((p) => p.service_id === formData.service_id).length;
  }, [formData.service_id, existingPackages]);

  const createBlockedByLimit =
    !isEdit &&
    maxPackagesPerService != null &&
    formData.service_id &&
    packageCountForSelectedService >= maxPackagesPerService;
  
  // Memoized handlers to prevent unnecessary re-renders
  const handleArrayItemChange = useCallback((field, index, value) => {
    onArrayFieldChange(field, index, value);
  }, [onArrayFieldChange]);

  const handleImageSelect = useCallback((image) => {
    if (image && onImageSelect) {
      onImageSelect(image);
      setShowImageBrowser(false);
    }
  }, [onImageSelect]);

  const handleAddArrayItem = useCallback((field) => {
    onAddArrayItem(field);
  }, [onAddArrayItem]);

  const handleRemoveArrayItem = useCallback((field, index) => {
    onRemoveArrayItem(field, index);
  }, [onRemoveArrayItem]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4" 
      style={{ zIndex: 99999, position: 'fixed' }} 
      onClick={onClose}
    >
      <div className="relative w-full max-w-3xl shadow-lg rounded-md bg-white flex flex-col" style={{ height: '90vh', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-4 sm:p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {isEdit ? 'Edit Package' : 'Create New Package'}
          </h3>
          
          {selectedService && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Creating package for:</span> {selectedService.service_name}
              </p>
            </div>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto overflow-x-hidden" style={{ flex: '1 1 0%', minHeight: 0 }}>
          <div className="p-4 sm:p-6">
            <form id="package-form" onSubmit={onSubmit} className="space-y-6">
            {/* Basic Information Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                <span className="w-2 h-2 bg-primary-600 rounded-full mr-3"></span>
                Basic Information
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Package Name *
                  </label>
                  <Input
                    type="text"
                    value={formData.package_name}
                    onChange={(e) => onInputChange('package_name', e.target.value)}
                    placeholder="Enter package name"
                    required
                  />
                </div>

                {/* Package Image */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Package Image
                  </label>
                  <div className="flex items-center gap-4">
                    {formData.image_url ? (
                      <div className="relative">
                        <img
                          src={formData.image_url}
                          alt="Package"
                          className="w-24 h-24 rounded-lg object-cover border-2 border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => onInputChange('image_url', '')}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-lg bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                        <Camera className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    <Button
                      type="button"
                      onClick={() => setShowImageBrowser(true)}
                      variant="outline"
                      size="sm"
                      icon={Camera}
                    >
                      {formData.image_url ? 'Change Image' : 'Select Image'}
                    </Button>
                  </div>
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service *
                  </label>
                  <SearchSelect
                    value={formData.service_id}
                    onChange={(value) => onInputChange('service_id', value)}
                    options={services.map(service => ({
                      value: service.id,
                      label: service.service_name
                    }))}
                    placeholder="Select a service"
                    required
                    disabled={!!selectedService}
                  />
                  {!isEdit && formData.service_id && maxPackagesPerService != null && (
                    <p className="text-xs text-gray-600 mt-1">
                      Packages on this service: {packageCountForSelectedService} / {maxPackagesPerService}
                    </p>
                  )}
                  {createBlockedByLimit && (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                      You&apos;ve reached the maximum packages allowed per service on your current plan.{' '}
                      <Link to="/vendor/subscription" className="font-medium underline hover:text-amber-900">
                        Upgrade your plan
                      </Link>{' '}
                      to add more packages to this service.
                    </div>
                  )}
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => onInputChange('description', e.target.value)}
                    placeholder="Describe your package in detail"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Pricing Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                <span className="w-2 h-2 bg-green-600 rounded-full mr-3"></span>
                Pricing
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Base Price ($) *
                  </label>
                  <Input
                    type="number"
                    value={formData.base_price}
                    onChange={(e) => onInputChange('base_price', e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                    hideSpinner={true}
                    preventTextInput={true}
                    formatNumber={true}
                    decimalPlaces={2}
                  />
                </div>
              </div>
            </div>

            {/* Package Details Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-900 mb-6 flex items-center">
                <span className="w-2 h-2 bg-blue-600 rounded-full mr-3"></span>
                Package Details
              </h4>
              
              {/* What's Included Subsection */}
              <div className="mb-6">
                <h5 className="text-sm font-medium text-gray-800 mb-3 flex items-center">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></span>
                  What's Included
                </h5>
                <div className="bg-white p-4 rounded-md border border-gray-200">
                  <MemoizedDynamicArrayField
                    label=""
                    field="includes_items"
                    placeholder="e.g., Photo booth, Basic props, Digital gallery"
                    description="List all items and services included in this package"
                    items={formData.includes_items}
                    onAdd={handleAddArrayItem}
                    onRemove={handleRemoveArrayItem}
                    onChange={handleArrayItemChange}
                  />
                </div>
              </div>

              {/* Package Features Subsection */}
              <div className="mb-6">
                <h5 className="text-sm font-medium text-gray-800 mb-3 flex items-center">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></span>
                  Package Features
                </h5>
                <div className="bg-white p-4 rounded-md border border-gray-200">
                  <MemoizedDynamicArrayField
                    label=""
                    field="package_features"
                    placeholder="e.g., Instant prints, Digital copies, Unlimited photos"
                    description="Highlight key features that make this package special"
                    items={formData.package_features}
                    onAdd={handleAddArrayItem}
                    onRemove={handleRemoveArrayItem}
                    onChange={handleArrayItemChange}
                  />
                </div>
              </div>

              {/* Add-on Options Subsection */}
              <div>
                <h5 className="text-sm font-medium text-gray-800 mb-3 flex items-center">
                  <span className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2"></span>
                  Add-on Options
                </h5>
                <div className="bg-white p-4 rounded-md border border-gray-200">
                  <MemoizedDynamicArrayField
                    label=""
                    field="addon_options"
                    placeholder="e.g., Extra hour (+$75), Premium props (+$50)"
                    description="List additional services customers can purchase with this package"
                    items={formData.addon_options}
                    onAdd={handleAddArrayItem}
                    onRemove={handleRemoveArrayItem}
                    onChange={handleArrayItemChange}
                  />
                </div>
              </div>
            </div>

            {/* Payment Schedule Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-900 mb-2 flex items-center">
                <span className="w-2 h-2 bg-amber-500 rounded-full mr-3"></span>
                Payment Schedule
              </h4>
              <p className="text-xs text-gray-500 mb-4">
                Clients see installments based on how far their event date is from today. Amounts update when they pick a date.
              </p>
              <PaymentScheduleRulesEditor
                value={formData.payment_schedule_rules}
                onChange={onPaymentScheduleRulesChange}
                samplePrice={parseFloat(formData.base_price) || 370}
              />
            </div>

            {/* Settings Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                <span className="w-2 h-2 bg-purple-600 rounded-full mr-3"></span>
                Settings
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_popular}
                      onChange={(e) => onInputChange('is_popular', e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Popular Package</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Popular packages are highlighted to customers
                  </p>
                </div> */}
                
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => onInputChange('is_active', e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Active Package</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Only active packages are visible to customers
                  </p>
                </div>
              </div>
            </div>
            </form>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex-shrink-0 p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="package-form"
              disabled={isLoading || createBlockedByLimit}
              loading={isLoading}
              className="w-full sm:w-auto"
            >
              {isEdit ? 'Update Package' : 'Create Package'}
            </Button>
          </div>
        </div>
      </div>

      {/* Image Browser Modal */}
      {showImageBrowser && (
        <div className="fixed inset-0 z-[10000] overflow-y-auto" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowImageBrowser(false);
          }
        }}>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity pointer-events-none"
          ></div>
          
          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-auto" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Select Package Image</h3>
                <button
                  onClick={() => setShowImageBrowser(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-xs text-gray-500 mb-3">
                  General uploads only — portfolio images are not listed here. New uploads are saved as general images.
                </p>
                <ImageBrowser
                  listQueryParams={{ non_portfolio: true }}
                  uploadImageType="general"
                  showUpload
                  showDelete
                  showSetDefault={false}
                  onSelectImage={handleImageSelect}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicePackageModal;
